import { BorderRadius } from '@persona-id/design-tokens';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { Elevation } from 'design-system/variables/effect';

import Button, { ButtonProps } from './Button';

const btnSizes = {
  sm: '12px',
  md: '16px',
};

type IconBtnSize = keyof typeof btnSizes;

const IconBtn = styled(Button).attrs({
  width: 'icon',
  variant: 'basic',
})`
  background-color: transparent;
  padding: 0;
  border-radius: ${BorderRadius.Circle};

  ${({ size }) => css`
    height: ${btnSizes[size as IconBtnSize]};
    width: ${btnSizes[size as IconBtnSize]};

    svg {
      width: ${btnSizes[size as IconBtnSize]};
      height: ${btnSizes[size as IconBtnSize]};
    }
  `}

  ${({ theme }) => css`
    &${isFocused}${isNotDisabled} {
      box-shadow: 0 0 0 3px ${theme.color.border.active};
    }
    &${isHovered}${isFocused}${isNotDisabled} {
      box-shadow:
        0 0 0 3px ${theme.color.border.active},
        ${Elevation.Mid};
    }
  `}
`;

export interface IconButtonProps
  extends Omit<ButtonProps, 'width' | 'prefixIcon' | 'prefixIconType' | 'suffixIcon' | 'variant'> {
  /**
   * `sm` - 12px. `md` - 16px. `lg` - 24px. Pair with a PDS icon in the prescribed size.
   */
  size?: 'sm' | 'md';
}

/**
 * Use to render icon-only buttons. Ensure buttons have proper aria properties.
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { size = 'md', ...props },
  ref,
) {
  return <IconBtn size={size} ref={ref} {...props} />;
});

export default IconButton;
